import {
  Alert,
  Button,
  Card,
  Form,
  Input,
  Space,
  Switch,
  Tooltip,
} from "antd";
import { EditOutlined, SnippetsOutlined } from "@ant-design/icons";
import type { FormInstance } from "antd";

const { TextArea } = Input;

type TextAreaRefs = {
  [key: string]: React.RefObject<any>;
};

type SmsTemplatesPanelProps = {
  smsEnabled: boolean;
  form?: FormInstance;
  formData?: any;
  formDisabled?: boolean;
  loading?: boolean;
  layout?: object;
  tailLayout?: object;
  textAreaRefs: TextAreaRefs;
  showEditControls: boolean;
  onToggleEdit: () => void;
  onAddVariable: () => void;
  onSmsEnabledChange: (checked: boolean) => void;
  onSelectTextArea: (name: string) => void;
  onValuesChange?: () => void;
  onSubmit?: (values: any) => void;
  showSubmit?: boolean;
  embedded?: boolean;
  onSelectTextArea: (name: string) => void;
};

const SmsTemplateFields: React.FC<
  Pick<
    SmsTemplatesPanelProps,
    | "smsEnabled"
    | "textAreaRefs"
    | "onSmsEnabledChange"
    | "onSelectTextArea"
    | "formDisabled"
  >
> = ({
  smsEnabled,
  textAreaRefs,
  onSmsEnabledChange,
  onSelectTextArea,
  formDisabled,
}) => (
  <>
    <div className="card-content">
      <Form.Item name="SmsEnabled" label="SMS Enabled" valuePropName="checked">
        <Switch onChange={onSmsEnabledChange} />
      </Form.Item>
    </div>
    {smsEnabled && (
      <>
        <div className="card-content">
          <Form.Item
            name="NewPolicySms"
            label="SMS New Policy"
            rules={[{ required: true }]}
          >
            <TextArea
              ref={textAreaRefs.NewPolicySms}
              size="large"
              placeholder="SMS New Policy"
              showCount
              maxLength={160}
              onFocus={() => onSelectTextArea("NewPolicySms")}
            />
          </Form.Item>
        </div>
        <div className="card-content">
          <Form.Item
            name="DocumentSms"
            label="Document SMS"
            rules={[{ required: true }]}
          >
            <TextArea
              ref={textAreaRefs.DocumentSms}
              size="large"
              placeholder="Document SMS"
              showCount
              maxLength={160}
              onFocus={() => onSelectTextArea("DocumentSms")}
            />
          </Form.Item>
        </div>
        <div className="card-content">
          <Form.Item
            name="NonPaymentSms"
            label="SMS Non-Payment"
            rules={[{ required: true }]}
          >
            <TextArea
              ref={textAreaRefs.NonPaymentSms}
              size="large"
              placeholder="SMS Non-Payment"
              showCount
              maxLength={160}
              onFocus={() => onSelectTextArea("NonPaymentSms")}
            />
          </Form.Item>
        </div>
        <div className="card-content">
          <Form.Item
            name="SmsLapse"
            label="SMS Policy Lapse"
            rules={[{ required: true }]}
          >
            <TextArea
              ref={textAreaRefs.SmsLapse}
              size="large"
              placeholder="SMS Policy Lapse"
              showCount
              maxLength={160}
              onFocus={() => onSelectTextArea("SmsLapse")}
            />
          </Form.Item>
        </div>
      </>
    )}
  </>
);

export const SmsTemplatesPanelFields = SmsTemplateFields;

const SmsTemplatesPanel: React.FC<SmsTemplatesPanelProps> = ({
  smsEnabled,
  form,
  formData,
  formDisabled = true,
  loading = false,
  layout = {},
  tailLayout = {},
  textAreaRefs,
  showEditControls,
  onToggleEdit,
  onAddVariable,
  onSmsEnabledChange,
  onSelectTextArea,
  onValuesChange,
  onSubmit,
  showSubmit = false,
  embedded = false,
}) => {
  const cardExtra = showEditControls ? (
    <>
      <Tooltip title="Add Policy Holder Variable">
        <SnippetsOutlined
          onClick={onAddVariable}
          style={{ color: "green", marginRight: 8 }}
        />
      </Tooltip>
      <Tooltip title="Edit SMS Templates">
        <EditOutlined onClick={onToggleEdit} style={{ color: "blue" }} />
      </Tooltip>
    </>
  ) : null;

  const fields = (
    <SmsTemplateFields
      smsEnabled={smsEnabled}
      textAreaRefs={textAreaRefs}
      onSmsEnabledChange={onSmsEnabledChange}
      onSelectTextArea={onSelectTextArea}
      formDisabled={formDisabled}
    />
  );

  if (embedded) {
    return (
      <>
        {!smsEnabled && (
          <Alert
            type="info"
            showIcon
            message="SMS is not enabled for this group"
            description="Enable SMS below to configure templates and document rules."
            style={{ marginBottom: 16 }}
          />
        )}
        <Card title="SMS Templates" bordered hoverable extra={cardExtra}>
          <SmsTemplateFields
            smsEnabled={smsEnabled}
            textAreaRefs={textAreaRefs}
            onSmsEnabledChange={onSmsEnabledChange}
            onSelectTextArea={onSelectTextArea}
            formDisabled={formDisabled}
          />
        </Card>
      </>
    );
  }

  return (
    <>
      {!smsEnabled && (
        <Alert
          type="info"
          showIcon
          message="SMS is not enabled for this group"
          description="Enable SMS below to configure templates and document rules."
          style={{ marginBottom: 16 }}
        />
      )}
      <Card title="SMS Templates" bordered hoverable extra={cardExtra}>
        <Form
          {...layout}
          onFinish={onSubmit}
          initialValues={formData}
          disabled={formDisabled}
          form={form}
          onValuesChange={onValuesChange}
        >
          {fields}
          {showSubmit && !formDisabled && (
            <div className="card-content">
              <Form.Item {...tailLayout}>
                <Space direction="vertical" style={{ width: "100%" }}>
                  <Button
                    block
                    size="large"
                    htmlType="submit"
                    loading={loading}
                  >
                    Submit
                  </Button>
                </Space>
              </Form.Item>
            </div>
          )}
        </Form>
      </Card>
    </>
  );
};

export default SmsTemplatesPanel;
